/**
 * BillingApi over fetch (contracts routes getBilling, createBillingCheckout, createBillingPortal) with the session's
 * access token: refreshed before it expires, and once more after a 401. Checkout and the portal return the Stripe page.
 */
import type { BillingRedirectResponse, BillingResponse } from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type BillingApi, type SessionViewModel } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

export function createBillingApi(session: SessionViewModel, base: string = API_BASE): BillingApi {
  async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown): Promise<T> {
    const route = routes[name];
    const url = base + buildPath(route.path, params);
    const send = async (token: string | undefined) => {
      const headers: Record<string, string> = { accept: "application/json" };
      if (body !== undefined) headers["content-type"] = "application/json";
      if (token !== undefined) headers.authorization = `Bearer ${token}`;
      try {
        return await fetch(url, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
    };
    await session.ensureFreshToken();
    let token = session.state.session?.token;
    let res = await send(token);
    if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
      token = session.state.session?.token;
      res = await send(token);
    }
    const text = await res.text();
    let json: unknown;
    try {
      json = text === "" ? undefined : JSON.parse(text);
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
      // 503 billing_unavailable is a configuration answer, not a flaky network.
      const kind = err?.code === "billing_unavailable" ? "server" : kindFor(res.status);
      throw new ApiFailure(kind, res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
    }
    return json as T;
  }

  return {
    billing: async (ws) => (await call<BillingResponse>("getBilling", { ws })).billing,
    checkout: async (ws, req) => (await call<BillingRedirectResponse>("createBillingCheckout", { ws }, req)).url,
    portal: async (ws) => (await call<BillingRedirectResponse>("createBillingPortal", { ws }, {})).url,
  };
}
